import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 10;

export default function PaiementSucces() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const scalapayToken = params.get("scalapay_token");
    const mode = params.get("mode");
    const [status, setStatus] = useState(mode === "pay_later" ? "pay_later" : mode === "subscription" ? "subscription" : "loading");

    useEffect(() => {
        const token = sessionId || scalapayToken;
        if (!token) return;
        const endpoint = scalapayToken ? `/scalapay/status/${scalapayToken}` : `/checkout/status/${sessionId}`;
        let attempts = 0;
        const poll = async () => {
            try {
                const { data } = await api.get(endpoint);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                attempts += 1;
                if (attempts >= MAX_POLLS) {
                    setStatus("timeout");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL);
            } catch (e) {
                console.error(e);
                attempts += 1;
                if (attempts >= MAX_POLLS) {
                    setStatus("error");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL);
            }
        };
        poll();
    }, [sessionId, scalapayToken]);

    return (
        <>
            <SEO title="Paiement confirmé — Mon Séraphin" description="Confirmation de votre paiement Studio Mon Séraphin." path="/paiement/succes" noindex />
            <Navbar />
            <main className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center" data-testid="payment-success-page">
                <div className="max-w-2xl mx-auto w-full text-center">
                    {status === "loading" && (
                        <div>
                            <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto" />
                            <h1 className="mt-8 font-display text-4xl text-white font-light">Vérification du paiement…</h1>
                            <p className="mt-3 text-zinc-500">Quelques secondes seulement.</p>
                        </div>
                    )}
                    {status === "paid" && (
                        <div data-testid="payment-paid">
                            <CheckCircle2 className="w-16 h-16 text-amber-400 mx-auto neon-text" />
                            <h1 className="mt-8 font-display text-5xl text-white font-light">Merci !</h1>
                            <p className="mt-4 text-zinc-400 text-lg">
                                Votre paiement est confirmé. Vous recevrez toutes les informations de votre session par email.
                            </p>
                            <Link to="/" className="mt-10 inline-flex rounded-full bg-amber-400 text-black px-6 py-3 font-medium hover:bg-amber-300 transition" data-testid="success-home-btn">
                                Retour à l'accueil
                            </Link>
                        </div>
                    )}
                    {status === "subscription" && (
                        <div data-testid="subscription-session-confirmed">
                            <CheckCircle2 className="w-16 h-16 text-blue-400 mx-auto" />
                            <h1 className="mt-8 font-display text-5xl text-white font-light">Session confirmée</h1>
                            <p className="mt-4 text-zinc-400 text-lg">
                                Votre créneau abonné est réservé. Un SMS de confirmation vous a été envoyé.
                            </p>
                            <Link to="/reserver?package=abonnement" className="mt-10 inline-flex rounded-full bg-blue-500 text-white px-6 py-3 font-medium hover:bg-blue-400 transition" data-testid="success-rebook-btn">
                                Réserver une autre session
                            </Link>
                        </div>
                    )}
                    {status === "pay_later" && (
                        <div data-testid="payment-paylater">
                            <Clock className="w-16 h-16 text-amber-400 mx-auto" />
                            <h1 className="mt-8 font-display text-5xl text-white font-light">Réservation enregistrée</h1>
                            <p className="mt-4 text-zinc-400 text-lg flex items-center justify-center gap-2">
                                <Mail className="w-4 h-4" />
                                Nous revenons vers vous sous 24h pour confirmer.
                            </p>
                            <Link to="/" className="mt-10 inline-flex rounded-full bg-amber-400 text-black px-6 py-3 font-medium hover:bg-amber-300 transition">
                                Retour à l'accueil
                            </Link>
                        </div>
                    )}
                    {(status === "expired" || status === "timeout" || status === "error") && (
                        <div>
                            <h1 className="font-display text-4xl text-white font-light">Paiement non finalisé</h1>
                            <p className="mt-3 text-zinc-500">
                                {status === "expired" ? "La session de paiement a expiré." : "Nous n'avons pas pu vérifier votre paiement."}
                            </p>
                            <Link to="/reserver" className="mt-10 inline-flex rounded-full bg-amber-400 text-black px-6 py-3 font-medium hover:bg-amber-300">Recommencer</Link>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </>
    );
}
